import {
  type Company,
  EXPIRY_STATE_LABELS,
  type ExpiryStateValue,
  GOODS_RECEIPT_STATUS_LABELS,
  type GoodsReceiptStatus,
  INVOICE_MATCH_STATUS_LABELS,
  type InvoiceMatchStatus,
  type MarketLedgerStatus,
  MATCH_LINE_STATUS_LABELS,
  type MatchLineStatusValue,
  PURCHASE_ORDER_STATUS_LABELS,
  type PurchaseOrderStatus,
  RECEIPT_COST_STATUS_LABELS,
  type ReceiptCostStatus,
  STATION_LICENSE_STATE_LABELS,
  type StationLicenseState,
  STOCK_COUNT_STATUS_LABELS,
  STOCK_TRANSFER_STATUS_LABELS,
  STOCK_WRITE_OFF_STATUS_LABELS,
  type StockCountStatus,
  type StockTransferStatus,
  type StockWriteOffStatus,
} from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, Fuel, ShoppingBasket } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { CompanyPicker, companySearchShape, useAccountingCompany } from "../accounting/company.js";
import { useCompanyStations } from "../accounting/shared.js";
import { useStationScope } from "../cashflow/shared.js";
import { LICENSE_STATE_TONE } from "./license-values.js";
import {
  COUNT_STATUS_TONE,
  EXPIRY_STATE_TONE,
  LEDGER_STATUS_TONE,
  movementSource,
  TRANSFER_STATUS_TONE,
  WRITE_OFF_STATUS_TONE,
} from "./market-values.js";
import { INVOICE_MATCH_STATUS_TONE, MATCH_LINE_STATUS_TONE } from "./match-values.js";
import {
  GOODS_RECEIPT_STATUS_TONE,
  PURCHASE_ORDER_STATUS_TONE,
  RECEIPT_COST_STATUS_TONE,
} from "./purchasing-values.js";
import { useMarketPermissions, useWarehouses } from "./queries.js";

/** Market ekranlarının ortak arama parametreleri: şirket (defter) ve istasyon (süzgeç). */
export const marketSearchShape = {
  ...companySearchShape,
  station: z.string().uuid().optional().catch(undefined),
};

export interface MarketContext {
  companyId: string;
  company: Company;
}

/**
 * Market ekranlarının ortak çerçevesi: başlık + ŞİRKET seçici (muhasebe / akaryakıt / kasa ekranlarıyla aynı
 * `?company=` parametresi) ve modül / izin kapıları. İstasyon burada KAPSAM değil SÜZGEÇTİR (stok listesi tüm
 * istasyonları gösterebilir), bu yüzden seçici sayfanın içinde durur.
 */
export function MarketPage({
  title,
  description,
  breadcrumb,
  actions,
  children,
}: {
  title: string;
  description?: string | undefined;
  /** "Market" kökünden SONRAKİ öğeler; verilmezse yalnızca sayfa başlığı eklenir. */
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode | ((context: MarketContext) => ReactNode);
  children: (context: MarketContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany();
  const permissions = useMarketPermissions();
  useDocumentTitle(title);
  const context = accounting.company
    ? { companyId: accounting.company.id, company: accounting.company }
    : null;
  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.market") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          <>
            <CompanyPicker accounting={accounting} />
            {context && permissions.enabled && permissions.canRead
              ? typeof actions === "function"
                ? actions(context)
                : actions
              : null}
          </>
        }
      />
      {!permissions.enabled ? (
        <EmptyState
          headingLevel={2}
          icon={<ShoppingBasket />}
          title={t("market.disabledTitle")}
          description={t("market.disabledDescription")}
        />
      ) : !permissions.canRead ? (
        <Alert tone="info">{t("market.noPermission")}</Alert>
      ) : accounting.error ? (
        <Alert tone="danger">{errorMessage(accounting.error)}</Alert>
      ) : accounting.isLoading ? (
        <SkeletonRows rows={6} />
      ) : context === null ? (
        <EmptyState
          headingLevel={2}
          icon={<Building2 />}
          title={t("accounting.company.noneTitle")}
          description={t("accounting.company.noneDescription")}
        />
      ) : (
        // Şirket değişince sayfanın yerel durumu (süzgeç, sekme, açık pencere) sıfırlanır.
        <div key={context.companyId} className="grid gap-4">
          {children(context)}
        </div>
      )}
    </>
  );
}

/** Market kökünden bir listeye geri bağlantılı breadcrumb öğesi (belge ayrıntıları için). */
export function crumbLink(
  label: string,
  to:
    | "/market/stok"
    | "/market/transferler"
    | "/market/fireler"
    | "/market/sayimlar"
    | "/market/hareketler"
    | "/market/siparisler"
    | "/market/mal-kabul"
    | "/market/eslestirme",
): BreadcrumbItem {
  return {
    label,
    link: (children, className) => (
      <Link to={to} className={className}>
        {children}
      </Link>
    ),
  };
}

export interface MarketStationOption {
  id: string;
  code: string;
  name: string;
}

export interface MarketStationSelection {
  stations: MarketStationOption[];
  /** Süzgeçte seçili istasyon; `null` = tümü. */
  stationId: string | null;
  /** Tek istasyonlu kiracıda ve görünür tek istasyonda seçici çizilmez (AGENTS.md §2). */
  showPicker: boolean;
  isLoading: boolean;
  error: unknown;
  select: (stationId: string | null) => void;
}

/**
 * İstasyon süzgeci (`?station=`): kapsamı kısıtlı kullanıcıya yalnızca kendi istasyonları gösterilir.
 * `required` → "tümü" seçeneği yoktur (sipariş önerisi, fiyat önerisi, etiket listesi İSTASYON bazındadır): seçim
 * yoksa ilk görünür istasyon kullanılır.
 */
export function useMarketStations(
  companyId: string,
  options: { required?: boolean } = {},
): MarketStationSelection {
  const stations = useCompanyStations(companyId);
  const scope = useStationScope();
  const shape = useTenantShape();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { station?: string };
  const visible = (stations.data ?? []).filter(
    (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
  );
  const selected = visible.find((station) => station.id === search.station) ?? null;
  return {
    stations: visible,
    // Tek görünür istasyonda süzgeç çizilmez ama sorgular yine o istasyona daraltılır.
    stationId: selected?.id ?? (visible.length === 1 || options.required ? (visible[0]?.id ?? null) : null),
    showPicker: visible.length > 1 && !shape.isSingleStation,
    isLoading: stations.isPending,
    error: stations.error,
    select: (stationId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({
          ...previous,
          station: stationId ?? undefined,
        })) as never,
        replace: true,
      }),
  };
}

export function MarketStationField({
  selection,
  className = "w-64",
  allowAll = true,
}: {
  selection: MarketStationSelection;
  className?: string;
  allowAll?: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!selection.showPicker) return null;
  return (
    <FormField label={t("market.fields.station")} className={className}>
      {(control) => (
        <Select
          {...control}
          value={selection.stationId ?? ""}
          onChange={(event) => selection.select(event.target.value || null)}
        >
          {allowAll ? <option value="">{t("common.all")}</option> : null}
          {selection.stations.map((station) => (
            <option key={station.id} value={station.id}>
              {station.code} {station.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

/** İstasyon seçiliyse o istasyonun depoları (süzgeç); istasyon yoksa çizilmez. */
export function WarehouseFilterField({
  companyId,
  stationId,
  value,
  onChange,
}: {
  companyId: string;
  stationId: string | null;
  value: string | undefined;
  onChange: (warehouseId: string | undefined) => void;
}): ReactElement | null {
  const { t } = useTranslation();
  const warehouses = useWarehouses(companyId, stationId, { enabled: stationId !== null });
  if (stationId === null || !warehouses.data || warehouses.data.length <= 1) return null;
  return (
    <FormField label={t("market.fields.warehouse")} className="w-48">
      {(control) => (
        <Select
          {...control}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value || undefined)}
        >
          <option value="">{t("common.all")}</option>
          {warehouses.data!.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.code} {warehouse.name}
            </option>
          ))}
        </Select>
      )}
    </FormField>
  );
}

/** Hiç istasyon görünmüyorsa (kapsam ya da kurulum eksik) ekranın geri kalanı çizilmez. */
export function NoStations(): ReactElement {
  const { t } = useTranslation();
  return (
    <EmptyState
      headingLevel={2}
      icon={<Fuel />}
      title={t("market.noStationsTitle")}
      description={t("market.noStationsDescription")}
      action={
        <Link to="/ayarlar/istasyonlar" className="text-sm underline underline-offset-4">
          {t("menu.stations")}
        </Link>
      }
    />
  );
}

// ── Rozetler ─────────────────────────────────────────────────────────────────
export function TransferStatusBadge({ status }: { status: StockTransferStatus }): ReactElement {
  return (
    <StatusBadge tone={TRANSFER_STATUS_TONE[status]}>{STOCK_TRANSFER_STATUS_LABELS[status]}</StatusBadge>
  );
}

export function WriteOffStatusBadge({ status }: { status: StockWriteOffStatus }): ReactElement {
  return (
    <StatusBadge tone={WRITE_OFF_STATUS_TONE[status]}>{STOCK_WRITE_OFF_STATUS_LABELS[status]}</StatusBadge>
  );
}

export function CountStatusBadge({ status }: { status: StockCountStatus }): ReactElement {
  return <StatusBadge tone={COUNT_STATUS_TONE[status]}>{STOCK_COUNT_STATUS_LABELS[status]}</StatusBadge>;
}

export function PurchaseOrderStatusBadge({ status }: { status: PurchaseOrderStatus }): ReactElement {
  return (
    <StatusBadge tone={PURCHASE_ORDER_STATUS_TONE[status]} className="whitespace-nowrap">
      {PURCHASE_ORDER_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function GoodsReceiptStatusBadge({ status }: { status: GoodsReceiptStatus }): ReactElement {
  return (
    <StatusBadge tone={GOODS_RECEIPT_STATUS_TONE[status]}>{GOODS_RECEIPT_STATUS_LABELS[status]}</StatusBadge>
  );
}

/** Mal kabul maliyeti: GEÇİCİ (sipariş / liste fiyatı) ya da faturayla eşleşince KESİN. */
export function ReceiptCostBadge({ status }: { status: ReceiptCostStatus }): ReactElement {
  return (
    <StatusBadge tone={RECEIPT_COST_STATUS_TONE[status]} className="whitespace-nowrap">
      {RECEIPT_COST_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function InvoiceMatchStatusBadge({ status }: { status: InvoiceMatchStatus }): ReactElement {
  return (
    <StatusBadge tone={INVOICE_MATCH_STATUS_TONE[status]} className="whitespace-nowrap">
      {INVOICE_MATCH_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

export function MatchLineStatusBadge({ status }: { status: MatchLineStatusValue }): ReactElement {
  return <StatusBadge tone={MATCH_LINE_STATUS_TONE[status]}>{MATCH_LINE_STATUS_LABELS[status]}</StatusBadge>;
}

export function LicenseStateBadge({ state }: { state: StationLicenseState }): ReactElement {
  return (
    <StatusBadge tone={LICENSE_STATE_TONE[state]} className="whitespace-nowrap">
      {STATION_LICENSE_STATE_LABELS[state]}
    </StatusBadge>
  );
}

/** SKT rozeti: anlam yalnızca renkle taşınmaz — rozet durumu METİNLE yazar; SKT'siz partide tire. */
export function ExpiryBadge({ state }: { state: ExpiryStateValue | null }): ReactElement {
  if (state === null) return <span className="text-muted-foreground">—</span>;
  return (
    <StatusBadge tone={EXPIRY_STATE_TONE[state]} className="whitespace-nowrap">
      {EXPIRY_STATE_LABELS[state]}
    </StatusBadge>
  );
}

/** Belgenin muhasebe fişi durumu; fiş kesildiyse fişe bağlantı. */
export function LedgerStatus({
  status,
  journalEntryId,
}: {
  status: MarketLedgerStatus;
  journalEntryId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  if (status === "NONE") return <span className="text-muted-foreground">—</span>;
  const badge = (
    <StatusBadge tone={LEDGER_STATUS_TONE[status]} className="whitespace-nowrap">
      {t(`market.ledger.${status}`)}
    </StatusBadge>
  );
  if (journalEntryId === null) return badge;
  return (
    <Link
      to="/muhasebe/fisler/$id"
      params={{ id: journalEntryId }}
      className="inline-flex items-center gap-1 underline underline-offset-4"
    >
      {badge}
      <span className="sr-only">{t("market.ledger.openEntry")}</span>
    </Link>
  );
}

/** Hareketin (ya da partinin) kaynak belgesine bağlantı; ekranı olmayan tür düz metin kalır. */
export function SourceDocLink({
  sourceDocType,
  sourceDocId,
  label,
}: {
  sourceDocType: string;
  sourceDocId: string;
  label: string;
}): ReactElement {
  const source = movementSource(sourceDocType, sourceDocId);
  const className = "underline underline-offset-4";
  if (source === null) return <span>{label}</span>;
  if (source.kind === "transfer")
    return (
      <Link to="/market/transferler/$id" params={{ id: source.id }} className={className}>
        {label}
      </Link>
    );
  if (source.kind === "writeOff")
    return (
      <Link to="/market/fireler/$id" params={{ id: source.id }} className={className}>
        {label}
      </Link>
    );
  if (source.kind === "count")
    return (
      <Link to="/market/sayimlar/$id" params={{ id: source.id }} className={className}>
        {label}
      </Link>
    );
  if (source.kind === "goodsReceipt")
    return (
      <Link to="/market/mal-kabul/$id" params={{ id: source.id }} className={className}>
        {label}
      </Link>
    );
  if (source.kind === "invoiceMatch")
    return (
      <Link to="/market/eslestirme/$id" params={{ id: source.id }} className={className}>
        {label}
      </Link>
    );
  return (
    <Link to="/kasa/satislar/$id" params={{ id: source.id }} className={className}>
      {label}
    </Link>
  );
}

/** Tanım listesi satırı (`<dl>` içinde kullanılır). */
export function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/** Kaydırılabilir tablo çerçevesi: klavyeyle odaklanıp kaydırılabilir (axe `scrollable-region-focusable`). */
export function TableFrame({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border" tabIndex={0} role="group" aria-label={label}>
      <table className="w-full text-sm">
        <caption className="sr-only">{label}</caption>
        {children}
      </table>
    </div>
  );
}
